"use client";

import * as React from "react";
import { cn } from "@/utils";

interface ChartCardProps {
  title: string;
  isLoading: boolean;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  accent?: string;
  className?: string;
  tall?: boolean;
}

export function ChartCard({
  title,
  isLoading,
  description,
  icon,
  children,
  accent,
  className,
  tall,
}: ChartCardProps) {
  return (
    <div
      className={cn(
        "lhx-card group flex h-full flex-col overflow-visible",
        tall ? "min-h-[28rem]" : "min-h-[22rem]",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-white/[0.05] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-surface-400/50 text-hex-400">
            {icon}
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight text-white">
              {title}
            </h2>
            <p className="mt-0.5 line-clamp-1 text-xs text-muted">
              {description}
            </p>
          </div>
        </div>
        {accent && !isLoading && (
          <span className="shrink-0 rounded-md border border-white/10 bg-surface-400/40 px-2.5 py-1 font-mono text-2xs uppercase tracking-wider text-muted">
            {accent}
          </span>
        )}
      </div>

      <div
        className={cn("relative flex-1 overflow-visible p-4", {
          "before:absolute before:inset-0 before:z-10 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/[0.03] before:to-transparent":
            isLoading,
        })}
      >
        {!isLoading && (
          <div className="h-full min-h-[14rem] overflow-visible">{children}</div>
        )}
        {isLoading && (
          <div className="flex h-full min-h-[14rem] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-surface-500 border-t-hex-400" />
          </div>
        )}
      </div>

      <div className="pointer-events-none absolute -inset-px -z-10 rounded-2xl hex-gradient opacity-0 blur-sm transition-opacity duration-500 group-hover:opacity-20" />
    </div>
  );
}

export default ChartCard;
